import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, CreditCard, FileDown, Sparkles, Upload, Database, HardDrive, File, Settings } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { getStorageStats, adminAPI } from "@/api/admin";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";

interface AppStats {
  totalReports: number;
  totalPDFs: number;
  totalProjects: number;
  storageUsed: number;
  storageLimit: number;
  storagePercentage: number;
  bucketStats: Array<{bucket: string, size: number, files: number}>;
  recentReports: {
    id: string;
    type: string;
    projectName: string;
    created_at: string;
  }[];
  dailyActivity: {
    date: string;
    count: number;
  }[];
}

const AppLogsPanel: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [timeframe, setTimeframe] = useState<'7days' | '30days' | '90days'>('30days');
  const [stats, setStats] = useState<AppStats>({
    totalReports: 0,
    totalPDFs: 0,
    totalProjects: 0,
    storageUsed: 0,
    storageLimit: 1 * 1024 * 1024 * 1024, // Default 1GB
    storagePercentage: 0,
    bucketStats: [],
    recentReports: [],
    dailyActivity: []
  });
  
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [storagePlan, setStoragePlan] = useState<'free' | 'pro' | 'team' | 'enterprise'>('free');
  const [customLimit, setCustomLimit] = useState<string>('10');
  const [customLimitUnit, setCustomLimitUnit] = useState<'GB' | 'TB'>('TB');
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const subscriptionRef = useRef<any>(null);

  useEffect(() => {
    fetchStats();
    // Get the current storage plan
    const currentPlan = adminAPI.getStoragePlan();
    setStoragePlan(currentPlan.planType);
    
    // If enterprise plan with custom limit, calculate the displayed value
    if (currentPlan.planType === 'enterprise') {
      const limitInGB = currentPlan.limit / (1024 * 1024 * 1024);
      if (limitInGB >= 1024) {
        setCustomLimit((limitInGB / 1024).toString());
        setCustomLimitUnit('TB');
      } else {
        setCustomLimit(limitInGB.toString());
        setCustomLimitUnit('GB');
      }
    }
    
    // Set up periodic refresh every 60 seconds for system stats
    intervalRef.current = setInterval(() => {
      fetchStats(true);
    }, 60000);

    // Set up real-time subscription for projects table changes (main data source)
    const setupRealtimeSubscription = async () => {
      try {
        subscriptionRef.current = supabase
          .channel('system-stats-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'projects'
            },
            (payload) => {
              
              // Debounce rapid changes by refreshing after a short delay
              setTimeout(() => {
                fetchStats(true);
              }, 2000);
            }
          )
          .subscribe();
      } catch (error) {
        
      }
    };

    setupRealtimeSubscription();

    // Cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, [timeframe, refreshTrigger]);

  const fetchStats = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      // Calculate date range based on timeframe
      const endDate = new Date();
      const startDate = new Date();
      
      if (timeframe === '7days') {
        startDate.setDate(endDate.getDate() - 7);
      } else if (timeframe === '30days') {
        startDate.setDate(endDate.getDate() - 30);
      } else {
        startDate.setDate(endDate.getDate() - 90);
      }
      
      const startDateStr = startDate.toISOString();
      
      // Get total projects - this is the only table we know for sure exists
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select('count');
        
      if (projectsError) throw projectsError;
      
      // Get Supabase storage statistics using the admin API
      const storageStats = await getStorageStats();
      
      // Calculate storage percentage
      const storagePercentage = storageStats.success 
        ? Math.min(100, Math.round((storageStats.size / storageStats.limit) * 100))
        : 0;
      
      // Simulate statistics for demonstration
      // In a real implementation, you would query actual tables
      const totalProjects = projectsData?.[0]?.count || 0;
      const totalReports = Math.floor(totalProjects * 1.5); // Simulated value
      const totalPDFs = Math.floor(totalProjects * 0.8); // Simulated value
      
      // Since activity_logs is not in the TypeScript definition for this project,
      // we'll generate simulated activity data based on projects for the chart
      let activityData = [];
      try {
        // Get project creation dates to simulate activity
        const { data: projectActivityData, error: projectActivityError } = await supabase
          .from('projects')
          .select('created_at')
          .gte('created_at', startDateStr);
          
        if (projectActivityError) {
          
        } else {
          // Use project data as activity data
          activityData = projectActivityData || [];
          
          // Add some additional random activities for variety
          const baseActivities = [...activityData];
          
          // Create some random activity entries spread over the time period
          const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
          for (let i = 0; i < daysDiff; i++) {
            const randomDay = new Date(startDate);
            randomDay.setDate(randomDay.getDate() + Math.floor(Math.random() * daysDiff));
            
            // Add 0-3 activities per day
            const activitiesCount = Math.floor(Math.random() * 4);
            for (let j = 0; j < activitiesCount; j++) {
              activityData.push({
                created_at: randomDay.toISOString()
              });
            }
          }
        }
      } catch (activityError) {
        
      }
      
      // Process activity data to get daily counts
      const dailyActivityMap = new Map<string, number>();
      
      // Initialize all dates in the range
      const dateIterator = new Date(startDate);
      while (dateIterator <= endDate) {
        const dateKey = dateIterator.toISOString().split('T')[0];
        dailyActivityMap.set(dateKey, 0);
        dateIterator.setDate(dateIterator.getDate() + 1);
      }
      
      // Count activities by day
      activityData.forEach(activity => {
        const date = new Date(activity.created_at);
        const dateKey = date.toISOString().split('T')[0];
        
        if (dailyActivityMap.has(dateKey)) {
          dailyActivityMap.set(dateKey, (dailyActivityMap.get(dateKey) || 0) + 1);
        }
      });
      
      // Convert map to array for display
      const dailyActivity = Array.from(dailyActivityMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => a.date.localeCompare(b.date));
      
      // Get recent projects as a sample of recent activity
      const { data: recentProjectsData, error: recentProjectsError } = await supabase
        .from('projects')
        .select('id, name, created_at')
        .order('created_at', { ascending: false })
        .limit(5);
        
      if (recentProjectsError) throw recentProjectsError;
      
      // Format recent projects as if they were reports
      const recentReports = recentProjectsData?.map(project => ({
        id: project.id,
        type: Math.random() > 0.5 ? 'detailed' : 'general', // Random type for demonstration
        projectName: project.name,
        created_at: project.created_at
      })) || [];
      
      setStats({
        totalReports,
        totalPDFs,
        totalProjects,
        storageUsed: storageStats.size,
        storageLimit: storageStats.limit,
        storagePercentage,
        bucketStats: storageStats.bucketStats,
        recentReports,
        dailyActivity
      });
      
      setLastUpdated(new Date());
    } catch (error) {
      
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    fetchStats(true);
  };

  const formatLastUpdated = () => {
    return lastUpdated.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Helper function to format bytes to MB or GB
  const formatStorageSize = (bytes: number) => {
    if (bytes < 1048576) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else if (bytes < 1073741824) {
      return (bytes / 1048576).toFixed(2) + ' MB';
    } else {
      return (bytes / 1073741824).toFixed(2) + ' GB';
    }
  };

  // Helper to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate max value for activity chart
  const maxActivityValue = Math.max(...stats.dailyActivity.map(d => d.count), 1);

  // Get storage usage color based on percentage
  const getStorageUsageColor = (percentage: number) => {
    if (percentage < 50) return "bg-green-500";
    if (percentage < 80) return "bg-yellow-500";
    return "bg-red-500";
  };

  // Handle storage plan updates
  const handleStoragePlanUpdate = async () => {
    setLoading(true);
    try {
      let calculatedLimit: number | undefined;
      
      // Calculate the limit based on the selected unit
      if (storagePlan === 'enterprise') {
        const numericLimit = parseFloat(customLimit);
        if (isNaN(numericLimit) || numericLimit <= 0) {
          toast({
            title: "Geçersiz Limit",
            description: "Lütfen geçerli bir sayı girin",
            variant: "destructive"
          });
          setLoading(false);
          return;
        }
        
        // Convert to bytes
        if (customLimitUnit === 'GB') {
          calculatedLimit = numericLimit * 1024 * 1024 * 1024;
        } else { // TB
          calculatedLimit = numericLimit * 1024 * 1024 * 1024 * 1024;
        }
      }
      
      // Update the storage plan
      const result = await adminAPI.updateStoragePlan(storagePlan, calculatedLimit);
      
      if (result.success) {
        toast({
          title: "Depolama Planı Güncellendi",
          description: result.message
        });
        // Refresh stats to show new limit
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast({
          title: "Hata",
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      
      toast({
        title: "Hata",
        description: "Plan güncellenirken bir hata oluştu",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Sistem İstatistikleri</h2>
        
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleManualRefresh}
            disabled={loading || refreshing}
          >
            {refreshing ? 'Yeniliyor...' : 'Yenile'}
          </Button>
          <span className="text-sm text-muted-foreground">Son Güncelleme: {formatLastUpdated()}</span>
          <Select 
            value={timeframe} 
            onValueChange={(value: '7days' | '30days' | '90days') => setTimeframe(value)}
          >
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Zaman Aralığı" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Son 7 Gün</SelectItem>
              <SelectItem value="30days">Son 30 Gün</SelectItem>
              <SelectItem value="90days">Son 90 Gün</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center">
              <CreditCard className="h-4 w-4 mr-2 text-primary" />
              Proje Sayısı
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.totalProjects}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center">
              <BarChart className="h-4 w-4 mr-2 text-blue-500" />
              Toplam Rapor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.totalReports}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center">
              <FileDown className="h-4 w-4 mr-2 text-green-500" />
              Oluşturulan PDF
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats.totalPDFs}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center">
              <Database className="h-4 w-4 mr-2 text-red-500" />
              Depolama Alanı
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-2xl font-bold">
              {formatStorageSize(stats.storageUsed)}
              <span className="text-sm text-muted-foreground ml-1">/ {formatStorageSize(stats.storageLimit)}</span>
            </p>
            <div className="w-full">
              <Progress 
                value={stats.storagePercentage} 
                className={`h-2 ${getStorageUsageColor(stats.storagePercentage)}`} 
              />
              <p className="text-xs text-right mt-1 text-muted-foreground">
                {stats.storagePercentage}% kullanıldı
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle>Depolama Ayrıntıları</CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Genel Bakış</TabsTrigger>
                <TabsTrigger value="buckets">Bucket Detayları</TabsTrigger>
                <TabsTrigger value="settings">Ayarlar</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="space-y-4">
                <div className="space-y-2 py-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Toplam Depolama</span>
                    <span className="font-medium">{formatStorageSize(stats.storageLimit)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Kullanılan</span>
                    <span className="font-medium">{formatStorageSize(stats.storageUsed)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Kalan</span>
                    <span className="font-medium">{formatStorageSize(stats.storageLimit - stats.storageUsed)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Kullanım Oranı</span>
                    <span className="font-medium">{stats.storagePercentage}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Bucket Sayısı</span>
                    <span className="font-medium">{stats.bucketStats.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Toplam Dosya Sayısı</span>
                    <span className="font-medium">
                      {stats.bucketStats.reduce((total, bucket) => total + bucket.files, 0)}
                    </span>
                  </div>
                </div>
                
                {/* Storage usage visualization */}
                <div className="pt-2">
                  <p className="text-sm text-muted-foreground mb-2">Depolama Kullanımı</p>
                  <div className="w-full bg-muted rounded-full h-4 overflow-hidden">
                    <div 
                      className={`h-full ${getStorageUsageColor(stats.storagePercentage)}`}
                      style={{ width: `${stats.storagePercentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>0</span>
                    <span>{formatStorageSize(stats.storageLimit)}</span>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="buckets">
                <div className="space-y-4">
                  {loading ? (
                    <div className="py-8 text-center text-muted-foreground">
                      Bucket bilgileri yükleniyor...
                    </div>
                  ) : stats.bucketStats.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Bucket</TableHead>
                          <TableHead>Boyut</TableHead>
                          <TableHead>Dosya Sayısı</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stats.bucketStats.map(bucket => (
                          <TableRow key={bucket.bucket}>
                            <TableCell className="font-medium">
                              <div className="flex items-center">
                                <HardDrive className="h-3 w-3 mr-2" />
                                {bucket.bucket}
                              </div>
                            </TableCell>
                            <TableCell>{formatStorageSize(bucket.size)}</TableCell>
                            <TableCell>
                              <div className="flex items-center">
                                <File className="h-3 w-3 mr-2" />
                                {bucket.files}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="space-y-6">
                      <div className="py-8 text-center text-muted-foreground">
                        <p className="mb-4">Bucket bilgisi bulunamadı veya yetki hatası</p>
                        <p className="text-sm">Bu sorun, Supabase Admin API'ye erişim veya veritabanı izni ile ilgili olabilir.</p>
                      </div>
                      
                      <div className="space-y-3 border p-4 rounded-md">
                        <h3 className="text-md font-medium">Sorun Giderme Bilgileri</h3>
                        <div className="space-y-2 text-sm">
                          <p>
                            <span className="font-semibold">Bilinen Bucketlar:</span> project-files, site-photos, profile-avatars
                          </p>
                          <p>
                            <span className="font-semibold">Servis Rolü Anahtarı:</span> {import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY ? "Mevcut ✅" : "Eksik ❌"}
                          </p>
                          <p>
                            <span className="font-semibold">Supabase URL:</span> {import.meta.env.VITE_SUPABASE_URL || "Eksik ❌"}
                          </p>
                          <p className="text-xs text-muted-foreground mt-2">
                            Not: Bu bölümde teknik bilgiler gösterilmektedir. Geliştirici ekibiyle paylaşmanız uygundur.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => setRefreshTrigger(prev => prev + 1)}
                          disabled={loading}
                          size="sm"
                          variant="outline"
                        >
                          Bilgileri Yenile
                        </Button>
                        <Button 
                          onClick={async () => {
                            setLoading(true);
                            try {
                              
                              const storageStats = await getStorageStats();
                              
                              
                              toast({
                                title: "Test Sonuçları",
                                description: `${storageStats.bucketStats.length} bucket bulundu. Konsolu kontrol edin.`,
                                variant: storageStats.bucketStats.length > 0 ? "default" : "destructive"
                              });
                            } catch (error) {
                              
                              toast({
                                title: "Test Hatası",
                                description: "Depolama istatistikleri alınırken hata oluştu. Konsolu kontrol edin.",
                                variant: "destructive"
                              });
                            } finally {
                              setLoading(false);
                            }
                          }}
                          disabled={loading}
                          size="sm"
                        >
                          Bağlantıyı Test Et
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="settings" className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Depolama Planı Ayarları</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="storage-plan">Plan Türü</Label>
                      <Select 
                        value={storagePlan} 
                        onValueChange={(value: 'free' | 'pro' | 'team' | 'enterprise') => setStoragePlan(value)}
                      >
                        <SelectTrigger id="storage-plan">
                          <SelectValue placeholder="Plan Seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Ücretsiz (1GB)</SelectItem>
                          <SelectItem value="pro">Pro (100GB)</SelectItem>
                          <SelectItem value="team">Team (1TB)</SelectItem>
                          <SelectItem value="enterprise">Enterprise (Özel Limit)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {storagePlan === 'enterprise' && (
                      <div className="grid grid-cols-5 gap-2 items-end">
                        <div className="col-span-3 space-y-2">
                          <Label htmlFor="custom-limit">Özel Limit</Label>
                          <Input 
                            id="custom-limit" 
                            type="number" 
                            value={customLimit}
                            onChange={(e) => setCustomLimit(e.target.value)}
                            min="1"
                            step="0.1"
                          />
                        </div>
                        <div className="col-span-2 space-y-2">
                          <Label htmlFor="custom-limit-unit">Birim</Label>
                          <Select 
                            value={customLimitUnit} 
                            onValueChange={(value: 'GB' | 'TB') => setCustomLimitUnit(value)}
                          >
                            <SelectTrigger id="custom-limit-unit">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="GB">GB</SelectItem>
                              <SelectItem value="TB">TB</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                    
                    <Button 
                      onClick={handleStoragePlanUpdate}
                      disabled={loading}
                      className="w-full"
                    >
                      <Settings className="h-4 w-4 mr-2" />
                      Plan Ayarlarını Güncelle
                    </Button>
                  </div>
                  
                  <div className="text-xs text-muted-foreground mt-4">
                    <p>Not: Bu ayarlar, görüntülenen depolama limitini değiştirir. 
                    Gerçek Supabase depolama limitinizi değiştirmez.</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Son Aktiviteler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] w-full">
              <div className="flex h-full items-end space-x-2">
                {stats.dailyActivity.map((day, index) => (
                  <div 
                    key={day.date} 
                    className="relative flex-1 flex flex-col items-center"
                    title={`${day.date}: ${day.count} aktivite`}
                  >
                    <div 
                      className="w-full bg-primary/80 rounded-sm" 
                      style={{ 
                        height: `${(day.count / maxActivityValue) * 100}%`,
                        minHeight: day.count > 0 ? '4px' : '0'
                      }}
                    ></div>
                    {(index % 4 === 0 || index === stats.dailyActivity.length - 1) && (
                      <span className="text-xs mt-2 absolute bottom-[-20px] text-muted-foreground">
                        {new Date(day.date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>Son Raporlar</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recentReports.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Proje</TableHead>
                    <TableHead>Tip</TableHead>
                    <TableHead>Tarih</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.recentReports.map(report => (
                    <TableRow key={report.id}>
                      <TableCell className="font-medium">
                        {report.projectName}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Sparkles className="h-3 w-3 mr-2 text-amber-500" />
                          {report.type === 'detailed' ? 'Detaylı' : 'Genel'}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatDate(report.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                Henüz rapor oluşturulmamış
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AppLogsPanel; 